import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  Alert,
  Anchor,
  Button,
  FileButton,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconUpload, IconPhonePause } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import { VariableTextarea } from '../../components/flow-editor/variable-textarea';
import { useOrg } from '../../hooks/use-org';
import { getErrorMessage } from '../../lib/error-message';
import { useAudioFiles, uploadAudioFile } from '../audio-files';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { voicemailWidgetCore, type VoicemailConfig } from '@legacy-shared/widgets/core/voicemail';
import { getPlayAudioSource, getPlayCustomUrl, type PlayConfig } from '@legacy-shared/widgets/core/play';

export { type VoicemailConfig } from '@legacy-shared/widgets/core/voicemail';

function getSubtitle(config: VoicemailConfig): string {
  const parts: string[] = [];
  if (config.greetingType === 'say') parts.push('Say greeting');
  else if (config.greetingType === 'play') parts.push('Play greeting');
  parts.push(`${config.maxDuration}s max`);
  return parts.join(' · ');
}

function VoicemailNode({ id, data, selected }: WidgetNodeProps<VoicemailConfig>) {
  return (
    <WidgetNodeShell
      icon={IconPhonePause}
      title={data.label || 'Voicemail'}
      subtitle={getSubtitle(data.config)}
      configured={true}
      errors={data.errors}
      selected={selected}
      color="grape"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="recorded" label="Recorded" offset="35%" nodeId={id} />
      <LabeledHandle type="source" position="bottom" id="no-message" label="No Message" offset="65%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="error" label="Error" offset="50%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function VoicemailConfigPanel({
  config,
  onChange,
  availableVariables,
  languages,
  defaultLanguage,
}: ConfigPanelProps<VoicemailConfig>) {
  const { organizationId, currentOrg } = useOrg();
  const audioFiles = useAudioFiles(organizationId, config.greetingType === 'play');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const isOrgAdmin = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';
  const source = getPlayAudioSource(config as unknown as PlayConfig);
  const audioFileOptions = (audioFiles ?? []).map((audioFile) => ({
    value: audioFile.id,
    label: audioFile.name,
  }));

  async function handleUpload(file: File | null) {
    if (!file || !organizationId || !isOrgAdmin) return;

    setUploadError(null);
    setIsUploading(true);

    try {
      const audioFile = await uploadAudioFile(organizationId, file);
      onChange({
        ...config,
        source: 'catalog',
        audioFileId: audioFile.id,
        audioFileName: audioFile.name,
        audioUrl: audioFile.audioUrl,
      });
    } catch (error) {
      setUploadError(getErrorMessage(error, 'Upload failed'));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Stack gap="md">
      <div>
        <Text size="sm" fw={500} mb={4}>
          Greeting
        </Text>
        <SegmentedControl
          fullWidth
          value={config.greetingType}
          onChange={(v) => onChange({ ...config, greetingType: v as VoicemailConfig['greetingType'] })}
          data={[
            { value: 'none', label: 'None' },
            { value: 'say', label: 'Say' },
            { value: 'play', label: 'Play Audio' },
          ]}
        />
      </div>

      {config.greetingType === 'say' && (
        <VariableTextarea
          label="Greeting Message"
          description="Spoken to the caller before recording begins"
          placeholder="Please leave a message after the beep."
          value={config.greeting}
          onChange={(greeting) => onChange({ ...config, greeting })}
          availableVariables={availableVariables}
          languages={languages}
          defaultLanguage={defaultLanguage}
          minRows={3}
        />
      )}

      {config.greetingType === 'play' && (
        <Stack gap="xs">
          <SegmentedControl
            value={source}
            onChange={(value) =>
              onChange({
                ...config,
                source: value as VoicemailConfig['source'],
                customUrl:
                  value === 'custom_url' ? getPlayCustomUrl(config as unknown as PlayConfig) : config.customUrl,
              })
            }
            data={[
              { label: 'Media Catalog', value: 'catalog' },
              { label: 'Custom URL', value: 'custom_url' },
            ]}
          />

          {source === 'catalog' ? (
            <Stack gap="xs">
              <Select
                label="Audio Media"
                placeholder={audioFiles?.length ? 'Select audio media' : 'Upload audio to get started'}
                data={audioFileOptions}
                value={config.audioFileId || null}
                onChange={(value) => {
                  const audioFile = audioFiles?.find((item) => item.id === value);
                  onChange({
                    ...config,
                    source: 'catalog',
                    audioFileId: value ?? '',
                    audioFileName: audioFile?.name ?? '',
                    audioUrl: audioFile?.audioUrl ?? '',
                  });
                }}
                searchable
                clearable
                disabled={!organizationId}
                rightSection={audioFiles === undefined ? <Loader size="xs" /> : undefined}
              />
              <Group justify="space-between" align="center">
                <Text size="sm" c="dimmed">
                  {audioFiles?.length
                    ? `${audioFiles.length} audio item${audioFiles.length === 1 ? '' : 's'} available`
                    : 'No audio uploaded yet'}
                </Text>
                {isOrgAdmin ? (
                  <FileButton onChange={handleUpload} accept="audio/*,.aac,.flac,.m4a,.mp3,.ogg,.wav,.webm">
                    {(fileButtonProps) => (
                      <Button
                        {...fileButtonProps}
                        variant="light"
                        leftSection={<IconUpload size={16} />}
                        loading={isUploading}
                        disabled={!organizationId}
                      >
                        Upload Audio
                      </Button>
                    )}
                  </FileButton>
                ) : null}
              </Group>
              {isOrgAdmin ? (
                <Anchor component={Link} to="/settings/media" size="sm">
                  Open media management
                </Anchor>
              ) : null}
              {uploadError ? (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                  {uploadError}
                </Alert>
              ) : null}
            </Stack>
          ) : (
            <TextInput
              label="Custom Audio URL"
              placeholder="https://example.com/greeting.mp3"
              value={getPlayCustomUrl(config as unknown as PlayConfig)}
              onChange={(event) =>
                onChange({
                  ...config,
                  source: 'custom_url',
                  customUrl: event.currentTarget.value,
                })
              }
            />
          )}
        </Stack>
      )}

      <NumberInput
        label="Max Duration"
        description="Maximum recording length in seconds"
        value={config.maxDuration}
        onChange={(value) => onChange({ ...config, maxDuration: typeof value === 'number' ? value : 120 })}
        min={1}
        max={600}
        suffix=" seconds"
      />

      <NumberInput
        label="Silence Timeout"
        description="How long to wait in silence before ending the recording"
        value={config.silenceTimeoutSeconds ?? 5}
        onChange={(value) => onChange({ ...config, silenceTimeoutSeconds: typeof value === 'number' ? value : 5 })}
        min={1}
        max={30}
        suffix=" seconds"
      />

      <Switch
        label="Play Beep"
        description="Play a beep sound before recording starts"
        checked={config.playBeep}
        onChange={(event) => onChange({ ...config, playBeep: event.currentTarget.checked })}
      />
    </Stack>
  );
}

function getPreviewText(config: VoicemailConfig): string {
  const parts: string[] = ['Voicemail'];
  if (config.greetingType !== 'none') {
    parts.push(`(${config.greetingType} greeting)`);
  }
  parts.push(`${config.maxDuration}s`);
  return parts.join(' ');
}

export const voicemailWidget: WidgetDefinition<VoicemailConfig> = {
  ...voicemailWidgetCore,
  icon: 'IconPhonePause',
  variables: voicemailWidgetCore.variables,

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'recorded', type: 'success', label: 'Recorded', position: 'bottom' },
      { id: 'no-message', type: 'failure', label: 'No Message', position: 'bottom' },
      { id: 'error', type: 'failure', label: 'Error', position: 'right' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: VoicemailNode,
    configPanel: VoicemailConfigPanel,
    preview: getPreviewText,
  },

  validation: {
    ...voicemailWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
